import { useMutation, useQueryClient } from '@tanstack/react-query'
import { getApiError } from '~/api/problem'
import {
  type ApiResponseError,
  fetchApiWithToken,
  type ResponseError
} from '~/api/utils'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import { ActionErrorType } from '~/domains/actions/errors'
import {
  type OptimisticEventContext,
  useAddOptimisticHistoryEvents,
  useRollbackOptimisticHistoryEvents,
  useUpdateOptimisticEventsWithRealIds
} from '~/domains/actions/hooks/optimisticHistoryUpdates'
import {
  generateCallEvent,
  generateEmailEvent,
  generateMeetingEvent,
  generateMessageEvent,
  generateNoteEvent
} from '~/domains/actions/utils/historyEventGenerator'
import type { OrderHead } from '~/domains/orders/types'
import { useTaxonomy } from '~/domains/taxonomies/queries/useTaxonomy'
import type { UserHeadResponse } from '~/domains/users/types'
import { toast } from '~/utils'
import { toISOLocal } from '~/utils/date'

type ActionResponse = {
  id: number
  user: UserHeadResponse | string
  datetime: string
  completionDate: string | null
  notes: string
  created: string
  applicants: Array<{ id: number }>
  contacts: Array<{ id: number }>
  isCompleted: boolean | null
  completedBy: UserHeadResponse | string | null
  actionType: number
}

type ActionsApiResponse = {
  actions: ActionResponse[]
}

type NewCallBasePayload = {
  applicants: number[]
  contacts?: number[]
  notes?: string
  actionType?: string
  orderId?: number
  assignedUserId?: number
}

export type NewCallPayload =
  | (NewCallBasePayload & {
      datetime: Date
    })
  | (NewCallBasePayload & {
      isCompleted: boolean
      /** Defaults to now on the server when omitted. */
      completionDate?: Date
    })

export const useNewCallMutation = () => {
  const addOptimisticHistoryEvents = useAddOptimisticHistoryEvents()
  const rollbackOptimisticHistoryEvents = useRollbackOptimisticHistoryEvents()
  const updateOptimisticEventsWithRealIds =
    useUpdateOptimisticEventsWithRealIds()
  const { data: actionTypes } = useTaxonomy('action_types', {
    context: 'applicant'
  })
  const queryClient = useQueryClient()

  return useMutation<
    ActionsApiResponse,
    ApiResponseError,
    NewCallPayload,
    {
      optimisticEvents: OptimisticEventContext[]
    }
  >({
    mutationFn: (payload) =>
      fetchApiWithToken({
        method: 'post',
        url: '/v1/actions/calls',
        data: {
          applicants: payload.applicants.map((id) => `/v1/applicants/${id}`),
          contacts: (payload.contacts ?? []).map((id) => `/v1/contacts/${id}`),
          datetime:
            'datetime' in payload ? toISOLocal(payload.datetime) : undefined,
          notes: payload.notes,
          isCompleted:
            'isCompleted' in payload ? payload.isCompleted : undefined,
          completionDate:
            'completionDate' in payload && payload.completionDate
              ? toISOLocal(payload.completionDate)
              : undefined,
          actionType: payload.actionType
            ? `/v1/taxonomies/action_types/${payload.actionType}`
            : undefined,
          order: payload.orderId ? `/v1/orders/${payload.orderId}` : undefined,
          assignedUser: payload.assignedUserId
            ? `/v1/users/${payload.assignedUserId}`
            : undefined
        }
      }),
    onMutate: (payload) => {
      const optimisticEvents = addOptimisticHistoryEvents(
        payload.applicants,
        (applicantIds: number[]) =>
          generateCallEvent(applicantIds, {
            datetime: 'datetime' in payload ? payload.datetime : undefined,
            notes: payload.notes,
            isCompleted:
              'isCompleted' in payload ? payload.isCompleted : undefined,
            actionType:
              payload.actionType &&
              actionTypes?.find(
                (type) => type.id === Number(payload.actionType)
              )?.name
          })
      )

      return { optimisticEvents }
    },
    onSuccess: (response, payload, context) => {
      queryClient.invalidateQueries({ queryKey: ['actions'] })
      for (const id of payload.applicants) {
        queryClient.invalidateQueries({
          queryKey: ['applicant', id, 'history']
        })
      }
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      if (context?.optimisticEvents && response.actions) {
        const realIds = response.actions.map((action) => action.id)
        updateOptimisticEventsWithRealIds(context.optimisticEvents, realIds)
      }
    },
    onError: (error, __, context) => {
      const problem = getApiError(error)
      if (problem?.type === ActionErrorType.LimitExceeded) {
        const limit = problem.extensions.limit
        toast(
          'Limite dépassée',
          typeof limit === 'number'
            ? `Vous ne pouvez pas créer plus de ${limit} appel${limit > 1 ? 's' : ''} à la fois`
            : "Vous ne pouvez pas créer autant d'appel à la fois",
          ToastType.Danger
        )
        return
      }
      if (context?.optimisticEvents) {
        rollbackOptimisticHistoryEvents(context.optimisticEvents)
      }

      toast(
        'Une erreur est survenue',
        'Une erreur est survenue lors de la création du rappel',
        ToastType.Danger
      )
    }
  })
}

export type NewNotePayload = {
  applicants: number[]
  contacts?: number[]
  notes: string
  actionType?: string
  orderId?: number
}

export const useNewNoteMutation = () => {
  const queryClient = useQueryClient()
  const addOptimisticHistoryEvents = useAddOptimisticHistoryEvents()
  const rollbackOptimisticHistoryEvents = useRollbackOptimisticHistoryEvents()
  const updateOptimisticEventsWithRealIds =
    useUpdateOptimisticEventsWithRealIds()
  const { data: actionTypes } = useTaxonomy('action_types', {
    context: 'applicant'
  })

  return useMutation<
    ActionsApiResponse,
    ResponseError,
    NewNotePayload,
    {
      optimisticEvents: OptimisticEventContext[]
    }
  >({
    mutationFn: ({ applicants, contacts, notes, actionType, orderId }) =>
      fetchApiWithToken({
        method: 'post',
        url: '/v1/actions/notes',
        data: {
          applicants: applicants.map((id) => `/v1/applicants/${id}`),
          contacts: (contacts ?? []).map((id) => `/v1/contacts/${id}`),
          notes,
          actionType: actionType
            ? `/v1/taxonomies/action_types/${Number(actionType)}`
            : undefined,
          order: orderId ? `/v1/orders/${orderId}` : undefined
        }
      }),
    onMutate: (payload) => {
      const optimisticEvents = addOptimisticHistoryEvents(
        payload.applicants,
        (applicantIds: number[]) =>
          generateNoteEvent(applicantIds, {
            notes: payload.notes,
            actionType:
              payload.actionType &&
              actionTypes?.find(
                (type) => type.id === Number(payload.actionType)
              )?.name
          })
      )

      return { optimisticEvents }
    },
    onSuccess: (response, payload, context) => {
      for (const id of payload.applicants) {
        queryClient.invalidateQueries({
          queryKey: ['applicant', id, 'history']
        })
      }
      if (context?.optimisticEvents && response.actions) {
        const realIds = response.actions.map((action) => action.id)
        updateOptimisticEventsWithRealIds(context.optimisticEvents, realIds)
      }
    },
    onError: (_, __, context) => {
      if (context?.optimisticEvents) {
        rollbackOptimisticHistoryEvents(context.optimisticEvents)
      }

      toast(
        'Une erreur est survenue',
        'Une erreur est survenue lors de la création de la note',
        ToastType.Danger
      )
    }
  })
}

type NewMessagePayload = {
  applicants?: number[]
  contacts?: number[]
  message?: string
  force?: boolean
  availabilityRequest?: boolean
}

export type NewMessageResponse = {
  needsConfirmation: boolean
  invalidApplicants: number[]
  invalidContacts: number[]
}

export const useNewMessageMutation = () => {
  const queryClient = useQueryClient()
  const addOptimisticHistoryEvents = useAddOptimisticHistoryEvents()
  const rollbackOptimisticHistoryEvents = useRollbackOptimisticHistoryEvents()

  return useMutation<
    NewMessageResponse,
    ApiResponseError,
    NewMessagePayload,
    {
      optimisticEvents: OptimisticEventContext[]
    }
  >({
    mutationFn: ({
      applicants,
      contacts,
      message,
      force,
      availabilityRequest
    }) =>
      fetchApiWithToken({
        method: 'post',
        url: '/v1/sms',
        data: {
          applicants: applicants?.map((id) => `/v1/applicants/${id}`),
          contacts: contacts?.map((id) => `/v1/contacts/${id}`),
          smsContent: message,
          force: force ?? false,
          availabilityRequest: availabilityRequest ?? false
        }
      }),
    onMutate: (payload) => {
      if (payload.availabilityRequest) return { optimisticEvents: [] }

      const optimisticEvents = addOptimisticHistoryEvents(
        payload.applicants ?? [],
        (applicantIds: number[]) =>
          generateMessageEvent(applicantIds, {
            message: payload.message ?? ''
          })
      )

      return { optimisticEvents }
    },
    onSuccess: (_, { applicants, contacts }) => {
      queryClient.invalidateQueries({
        queryKey: ['messaging', 'conversations']
      })
      for (const id of applicants ?? []) {
        queryClient.invalidateQueries({
          queryKey: ['applicant', id, 'history']
        })
      }
      for (const id of contacts ?? []) {
        queryClient.invalidateQueries({
          queryKey: ['contact', id, 'history']
        })
      }
    },
    onError: (_, __, context) => {
      if (context?.optimisticEvents) {
        rollbackOptimisticHistoryEvents(context.optimisticEvents)
      }
    }
  })
}

type EmailPayload = {
  applicants?: number[]
  contacts?: number[]
  /** IRIs (users/applicants/contacts) or raw email addresses */
  cc?: string[]
  /** IRIs (users/applicants/contacts) or raw email addresses */
  bcc?: string[]
  emailTo: 'applicant' | 'contact'
  subject?: string
  content: string
  templateId?: number | null
  userId?: number | null
  agencyId?: number | null
  order?: OrderHead | null
  attachments?: {
    filename: string
    base64_content: string
  }[]
  attachmentsIds?: number[]
  shortlistComments?: { applicantId: number; comment: string }[]
  notificationRead?: boolean
  notificationReceived?: boolean
  skipInvalidation?: boolean
  force?: boolean
}

export type NewEmailResponse = {
  needsConfirmation: boolean
  invalidApplicants: number[]
  invalidContacts: number[]
}

export const useNewEmailMutation = () => {
  const queryClient = useQueryClient()
  const addOptimisticHistoryEvents = useAddOptimisticHistoryEvents()
  const rollbackOptimisticHistoryEvents = useRollbackOptimisticHistoryEvents()

  return useMutation<
    NewEmailResponse,
    ApiResponseError,
    EmailPayload,
    {
      optimisticEvents: OptimisticEventContext[]
    }
  >({
    mutationFn: ({
      applicants,
      contacts,
      emailTo,
      userId,
      agencyId,
      templateId,
      order,
      ...rest
    }) => {
      const applicantUris = applicants?.map((id) => `/v1/applicants/${id}`)
      const contactUris = contacts?.map((id) => `/v1/contacts/${id}`)
      const involved = emailTo === 'applicant' ? contactUris : applicantUris
      const recipients = emailTo === 'applicant' ? applicantUris : contactUris
      return fetchApiWithToken({
        method: 'post',
        url: '/v1/emails',
        data: {
          involved: involved,
          recipients: recipients,
          user: userId ? `/v1/users/${userId}` : null,
          agency: agencyId ? `/v1/agencies/${agencyId}` : null,
          template: templateId ? `/v1/templates/${templateId}` : null,
          order: order ? `/v1/orders/${order.id}` : null,
          ...rest
        }
      })
    },
    onMutate: (payload) => {
      let optimisticEvents: OptimisticEventContext[] = []

      // Add optimistic event for each applicant (only if sending to applicants)
      if (payload.emailTo === 'applicant' && payload.applicants) {
        optimisticEvents = addOptimisticHistoryEvents(
          payload.applicants,
          (applicantIds: number[]) =>
            generateEmailEvent(applicantIds, {
              subject: payload.subject,
              content: payload.content,
              order: payload.order ?? undefined
            })
        )
      }

      return { optimisticEvents }
    },
    onError: (error, { emailTo }, context) => {
      const problem = getApiError(error)
      if (problem?.type === ActionErrorType.LimitExceeded) {
        const limit = problem.extensions.limit
        toast(
          'Limite dépassée',
          typeof limit === 'number'
            ? `Vous ne pouvez pas envoyer d'email à plus de ${limit} ${emailTo === 'applicant' ? 'candidat' : 'contact'}${limit > 1 ? 's' : ''} à la fois`
            : `Vous ne pouvez pas envoyer d'email à plus de 1 ${emailTo === 'applicant' ? 'candidat' : 'contact'} à la fois`,
          ToastType.Danger
        )
      }
      if (context?.optimisticEvents) {
        rollbackOptimisticHistoryEvents(context.optimisticEvents)
      }
    },
    onSuccess: (_, { applicants, contacts }) => {
      queryClient.invalidateQueries({
        queryKey: ['messaging', 'conversations']
      })
      for (const id of applicants ?? []) {
        queryClient.invalidateQueries({
          queryKey: ['applicant', id, 'history']
        })
      }
      for (const id of contacts ?? []) {
        queryClient.invalidateQueries({
          queryKey: ['contact', id, 'history']
        })
      }
    },
    onSettled: (_, __, { order, skipInvalidation }) => {
      if (skipInvalidation) {
        return
      }

      if (order) {
        // Email action can change applicant status on order
        queryClient.invalidateQueries({ queryKey: ['order', order.id] })
        queryClient.invalidateQueries({
          queryKey: ['orders', order.id, 'shortlist-user-comments']
        })
      }
    }
  })
}

export enum MeetingLocationType {
  ONLINE = 'online',
  AGENCY = 'agency',
  CONTACT = 'contact',
  ORDER = 'order',
  CUSTOM = 'custom'
}

export type MeetingLocationData =
  | {
      type: MeetingLocationType.AGENCY
      agencyId?: number
    }
  | {
      type: MeetingLocationType.CONTACT
      contactId?: number
    }
  | {
      type: MeetingLocationType.ORDER
      orderId?: number
    }
  | {
      type: MeetingLocationType.CUSTOM
      address?: string
    }
  | {
      type: MeetingLocationType.ONLINE
      details?: string
    }

export type NewMeetingPayload = {
  fromDatetime: Date
  toDatetime: Date
  subject?: string
  location: MeetingLocationData | null
  wholeDay?: boolean
  isPrivate?: boolean
  notes?: string
  orderId?: number
  applicants: number[]
  contacts: number[]
  /** Address-book values: IRIs (users/applicants/contacts) or raw email addresses */
  participants?: string[]
  isOnline?: boolean
  sendNotification?: boolean
  assignedUserId?: number
  invitation?: MeetingInvitationPayload
}

export type MeetingInvitationPayload = {
  subject: string
  content: string
  templateId?: number
  alertDaysBefore?: number
}

type NewMeetingResponse = ActionsApiResponse & {
  uninvitedRecipients: string[]
}

export const useNewMeetingMutation = () => {
  const queryClient = useQueryClient()
  const addOptimisticHistoryEvents = useAddOptimisticHistoryEvents()
  const rollbackOptimisticHistoryEvents = useRollbackOptimisticHistoryEvents()
  const updateOptimisticEventsWithRealIds =
    useUpdateOptimisticEventsWithRealIds()

  return useMutation<
    NewMeetingResponse,
    ApiResponseError,
    NewMeetingPayload,
    {
      optimisticEvents: OptimisticEventContext[]
    }
  >({
    mutationFn: ({
      fromDatetime,
      toDatetime,
      subject,
      location,
      wholeDay,
      isPrivate,
      notes,
      orderId,
      applicants,
      contacts,
      participants,
      isOnline,
      sendNotification,
      assignedUserId,
      invitation
    }) => {
      let addressOf: string | undefined
      let customAddress: string | undefined

      if (location?.type === MeetingLocationType.AGENCY && location.agencyId) {
        addressOf = `/v1/agencies/${location.agencyId}`
      } else if (
        location?.type === MeetingLocationType.CONTACT &&
        location?.contactId
      ) {
        addressOf = `/v1/contacts/${location.contactId}`
      } else if (
        location?.type === MeetingLocationType.ORDER &&
        location?.orderId
      ) {
        addressOf = `/v1/orders/${location.orderId}`
      } else if (
        location?.type === MeetingLocationType.CUSTOM &&
        location?.address
      ) {
        customAddress = location.address
      } else if (location?.type === MeetingLocationType.ONLINE) {
        customAddress = location?.details ?? ''
      }

      return fetchApiWithToken({
        method: 'post',
        url: '/v1/events/meetings',
        data: {
          subject,
          fromDatetime: toISOLocal(fromDatetime),
          toDatetime: toISOLocal(toDatetime),
          wholeDay: wholeDay ?? false,
          private: isPrivate ?? false,
          isOnline: isOnline ?? false,
          addressOf,
          customAddress,
          notes,
          order: orderId ? `/v1/orders/${orderId}` : null,
          applicants: applicants.map((id) => `/v1/applicants/${id}`),
          contacts: contacts?.map((id) => `/v1/contacts/${id}`),
          participants,
          sendNotification,
          assignedUser: assignedUserId
            ? `/v1/users/${assignedUserId}`
            : undefined,
          invitation: invitation
            ? {
                subject: invitation.subject,
                content: invitation.content,
                template: invitation.templateId
                  ? `/v1/templates/${invitation.templateId}`
                  : undefined,
                alertDaysBefore: invitation.alertDaysBefore
              }
            : undefined
        }
      })
    },
    onMutate: (payload) => {
      const optimisticEvents = addOptimisticHistoryEvents(
        payload.applicants,
        (applicantIds: number[]) =>
          generateMeetingEvent(applicantIds, {
            fromDatetime: payload.fromDatetime,
            toDatetime: payload.toDatetime,
            subject: payload.subject,
            notes: payload.notes,
            contacts: payload.contacts
          })
      )

      return { optimisticEvents }
    },
    onSuccess: (response, payload, context) => {
      queryClient.invalidateQueries({ queryKey: ['actions'] })
      for (const id of payload.applicants) {
        queryClient.invalidateQueries({
          queryKey: ['applicant', id, 'history']
        })
      }
      for (const id of payload.contacts) {
        queryClient.invalidateQueries({ queryKey: ['contact', id, 'history'] })
      }
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      if (context?.optimisticEvents && response.actions) {
        const realIds = response.actions.map((action) => action.id)
        updateOptimisticEventsWithRealIds(context.optimisticEvents, realIds)
      }
    },
    onError: (_, __, context) => {
      if (context?.optimisticEvents) {
        rollbackOptimisticHistoryEvents(context.optimisticEvents)
      }
    },
    onSettled: (_, __, { orderId }) => {
      if (orderId) {
        // Meeting action can change applicant status on order
        queryClient.invalidateQueries({ queryKey: ['order', orderId] })
      }
    }
  })
}
